import axios from 'axios';
import { getSessionCookie, removeSessionCookie } from './auth';
import { toastError, alertError } from './alerts';

const api = axios.create({
    baseURL: '/api'
});

// 1. Interceptor de Requisição
api.interceptors.request.use(async (config) => {
    const token = getSessionCookie();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// 2. Interceptor de Resposta
api.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const status = error.response?.status;

        if (status === 401) {
            if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
                console.warn("Sessão expirada. Realizando logout.");
                removeSessionCookie();
                window.location.href = '/login';
            }
            return Promise.reject(error);
        }

        // Alerta global de erro (todas as páginas). Use config.skipGlobalError para suprimir.
        if (!error.config?.skipGlobalError) {
            const mensagem = await extractErrorMessage(error);
            if (status >= 500) {
                // Falha do servidor: a mensagem traz o código de referência que
                // localiza o stack trace no log. Toast de 3,5s não dá tempo de
                // anotar, então exige o clique do usuário.
                alertError(mensagem, 'Erro no servidor');
            } else {
                toastError(mensagem);
            }
        }

        return Promise.reject(error);
    }
);

const extractErrorMessage = async (error) => {
    const res = error.response;
    if (!res) return 'Falha de conexão com o servidor.';

    let data = res.data;
    if (data instanceof Blob) {
        try { data = JSON.parse(await data.text()); } catch { data = null; }
    }
    const msg = data && (data.message || data.error || data.erro || data.mensagem);
    return msg || `Erro ${res.status}${res.statusText ? ': ' + res.statusText : ''}`;
};

// --- FUNÇÕES DE AUTENTICAÇÃO ---

export const loginUsuario = async (credentials, force = false) => {
    // tenant vai também na query: o back resolve o schema ANTES de abrir a
    // sessão (OSIV), pois no login ainda não há JWT.
    const tenant = encodeURIComponent(credentials.tenant || '');
    const response = await api.post(`/auth/login?force=${force}&tenant=${tenant}`, credentials, { skipGlobalError: true });
    return response.data;
};

export const logoutUsuario = async () => {
    // Chama o endpoint que limpa o token_ativo no banco de dados
    const response = await api.post('/auth/logout');
    return response.data;
};

export const validarSessao = async () => {
    const response = await api.get('/auth/validate');
    return response.data;
};

export const registrarUsuario = async (userData) => {
    // POST http://localhost:8080/api/auth/register
    const response = await api.post('/auth/register', userData);
    return response.data;
};

// --- FUNÇÕES UTILITÁRIAS ---

// Busca um PDF e devolve um object URL (blob:) para exibir em <iframe>/modal,
// sem abrir nova guia. O chamador é responsável por revogar o URL (revokeObjectURL).
// Erro propaga: o interceptor já exibe toast (parseando o Blob) e trata 401.
export const fetchPdfBlobUrl = async (url, params) => {
    const response = await api.get(url, {
        params: params,
        responseType: 'blob'
    });
    const file = new Blob([response.data], { type: 'application/pdf' });
    return URL.createObjectURL(file);
};

/**
 * Busca uma imagem servida pela API e devolve um object URL para usar em `src`.
 *
 * `<img src="/api/...">` é requisição do navegador: não passa pelo interceptor,
 * então vai sem o header Authorization. Sem JWT não há claim de tenant, o
 * TenantContext fica vazio e a consulta cai no schema de controle — que não tem
 * table_usuario nem table_parametro_sistema. Era o que fazia a foto do usuário e
 * o logo do consultório nunca carregarem, enchendo o log de 42P01.
 *
 * Devolve null quando não há imagem (404) ou o corpo vem vazio, para o chamador
 * cair no placeholder. Quem usa é responsável por revogar o URL.
 */
export const fetchImagemBlobUrl = async (url) => {
    try {
        const response = await api.get(url, { responseType: 'blob', skipGlobalError: true });
        if (!response.data || response.data.size === 0) return null;
        return URL.createObjectURL(response.data);
    } catch {
        return null;
    }
};

export const downloadArquivo = async (url, params, nomeArquivo = 'documento.pdf') => {
    const response = await api.get(url, {
        params: params,
        responseType: 'blob'
    });

    const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', nomeArquivo);
    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(blobUrl);
};

export default api;